
import { Loader2 } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { devLog } from "@/utils/devLog";
import { performance } from "@/utils/performance";
import { Button } from "@/components/ui/button";

export const CourseLoading = () => {
  const [loadingTime, setLoadingTime] = useState<number>(0);
  const startTimeRef = useRef<number>(Date.now());
  const [loadingStage, setLoadingStage] = useState<string>("Loading course content");
  const loadingTimeInterval = useRef<number | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  
  useEffect(() => {
    const startTime = performance.now();
    const displayedAt = Date.now();
    startTimeRef.current = displayedAt;
    
    devLog("[CourseLoading] Loading component displayed");
    
    // Start counter to show loading time to user
    loadingTimeInterval.current = window.setInterval(() => {
      const currentTime = Date.now();
      const elapsed = Math.floor((currentTime - displayedAt) / 1000);
      setLoadingTime(elapsed);
      
      // Update loading stage based on time
      if (elapsed > 6) {
        setLoadingStage("Still working on it, fetching course data");
      } else if (elapsed > 3) {
        setLoadingStage("Processing course data");
      }
      
      // If loading takes too long, show fallback options
      if (elapsed > 10 && !showFallback) {
        setShowFallback(true);
      }
    }, 1000);
    
    return () => {
      const duration = performance.now() - startTime;
      devLog("[CourseLoading] Loading component unmounted", {
        visibleDuration: `${duration.toFixed(1)}ms`,
        loadingTime: `${Math.floor((Date.now() - displayedAt) / 1000)}s`
      });
      
      if (loadingTimeInterval.current) {
        clearInterval(loadingTimeInterval.current);
        loadingTimeInterval.current = null;
      }
    };
  }, []);

  const handleForceClearCache = () => {
    // Clear all course-related data from localStorage
    try {
      const keysToRemove: string[] = [];
      
      // Find all course-related keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('course_')) {
          keysToRemove.push(key);
        }
      }
      
      // Remove them
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      devLog("[CourseLoading] Cleared course cache", { 
        itemsCleared: keysToRemove.length 
      });
      
      // Refresh the page
      window.location.reload();
    } catch (err) {
      console.error("Error clearing cache:", err);
    }
  };

  return (
    <div className="h-[400px] flex flex-col items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-muted-foreground mt-4">{loadingStage}...</p>
      {loadingTime > 2 && (
        <div className="mt-2 text-sm text-muted-foreground">
          Loading for {loadingTime} seconds
        </div>
      )}
      
      {showFallback && (
        <div className="mt-6 flex flex-col items-center gap-3">
          <p className="text-sm text-muted-foreground">
            This is taking longer than usual. You can try:
          </p>
          <div className="flex gap-3">
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              size="sm"
            >
              Refresh Page
            </Button>
            <Button
              onClick={handleForceClearCache}
              variant="outline"
              size="sm"
            >
              Clear Cache & Reload
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
